import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../../utils/Button';
import { GetCurrencySymbol } from '../../../../utils/Currency';
import { InputCheckbox } from '../../../../utils/InputField';
import { ImageUploadingIcon } from '../../../../utils/SVGIcon';
import { mkRelativeUrl } from '../../../../utils/URL';
import cn from '../../../../utils/cn';
import { ApiGetPurchaseServicesList } from '../../../Settings/Services/ServiceOperation';
import { ApiCreateServiceBuyInvoice } from '../CustomerDetailsOperation';

function Items({ arrayList, handlePackageSelect, handleCustomInput, checkedItems }) {
    if (arrayList?.length === 0) {
        return (
            <div className="flex justify-center items-center py-4 bg-secondary-100 rounded-10">
                <span className="text-gray-500">
                    No Package found for this service or user current currency!
                </span>
            </div>
        );
    }
    return (
        <ul className="w-full bg-secondary-50 rounded-10 divide-y">
            {arrayList?.map((option) => {
                return (
                    <li key={option.id} className="w-full flex flex-col p-3">
                        <span className="text-[18px] font-medium line-clamp-1">
                            {option?.serviceTitle}
                        </span>
                        <div className="ml-3 flex flex-col gap-2">
                            {option?.packages?.map((el) => {
                                return (
                                    <label
                                        htmlFor={el?.pricingId}
                                        key={el?.pricingId}
                                        className="size-full flex items-center justify-start gap-2  px-3 "
                                    >
                                        <input
                                            type="checkbox"
                                            id={el?.pricingId}
                                            onChange={(e) => handlePackageSelect(e, el, option)}
                                        />
                                        <div className="flex justify-between items-center w-full text-base font-normal">
                                            <span>{el?.packageName}</span>
                                            {el?.amount === -101 ? (
                                                <span>
                                                    {GetCurrencySymbol(el?.currency)}{' '}
                                                    <input
                                                        type="text"
                                                        onChange={(e) => handleCustomInput(e, el)}
                                                        ref={(input) => {
                                                            if (input) {
                                                                if (
                                                                    !checkedItems.includes(
                                                                        el.packageId
                                                                    )
                                                                ) {
                                                                    // eslint-disable-next-line no-param-reassign
                                                                    input.value = '';
                                                                }
                                                            }
                                                        }}
                                                        placeholder="Enter Amount"
                                                        className="w-30 h-8 p-1 rounded-5 border border-gray-300 focus:outline-none disabled:bg-primary-100"
                                                        disabled={
                                                            !checkedItems.includes(el.packageId)
                                                        }
                                                    />
                                                </span>
                                            ) : (
                                                <span>
                                                    {GetCurrencySymbol(el?.currency)}
                                                    {el?.amount || 0}
                                                </span>
                                            )}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function AddNewServiceModal({ closeModal, data }) {
    const { setMessage } = useTemplate();
    const { id, currency } = data;
    const [checkedItems, setCheckedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [servicesList, setServicesList] = useState([]);
    const [formData, setFormData] = useState([]);
    const [isInitiate, setIsInitiate] = useState(true);
    const navigate = useNavigate();

    // Fetch services list
    useEffect(() => {
        setIsLoading(true);
        // Fetch services list
        ApiGetPurchaseServicesList(id)
            .then(({ data: resData }) => {
                setServicesList(resData.services);
                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
                setMessage({ type: 'error', text: err.toString() });
            });
    }, [id, setMessage]);
    // select package item
    const handlePackageSelect = (e, packageItem, serviceItem) => {
        setCheckedItems((prev) => {
            if (prev.includes(packageItem.packageId)) {
                return prev.filter((el) => el !== packageItem.packageId);
            }
            return [...prev, packageItem.packageId];
        });
        const { checked } = e.target;
        if (checked) {
            if (packageItem?.amount === -101) {
                // if is already in the list then return
                if (formData.some((el) => el.AppServicePackageId === packageItem.packageId)) {
                    return null;
                }
                setFormData((prev) => [
                    ...prev,
                    {
                        AppServiceId: serviceItem.id,
                        AppServicePackageId: packageItem.packageId,
                        Amount: 0
                    }
                ]);
            } else {
                setFormData((prev) => [
                    ...prev,
                    {
                        AppServiceId: serviceItem.id,
                        AppServicePackageId: packageItem.packageId,
                        Amount: packageItem?.amount || 0
                    }
                ]);
            }
        }
        if (!checked) {
            setFormData((prev) =>
                prev.filter((el) => el.AppServicePackageId !== packageItem.packageId)
            );
        }

        return null;
    };

    // modifying payload
    const payload = useMemo(() => {
        return {
            CustomerId: id,
            PackageData: formData,
            Start: isInitiate
        };
    }, [formData, id, isInitiate]);

    // handle custom input
    const handleCustomInput = (e, packageItem) => {
        const { value } = e.target;
        const index = formData.findIndex((el) => el.AppServicePackageId === packageItem.packageId);
        if (index !== -1) {
            setFormData((prev) => {
                // eslint-disable-next-line no-param-reassign
                prev[index].Amount = Number(value) || 0;
                return [...prev];
            });
        } else {
            setFormData((prev) => [
                ...prev,
                {
                    AppServiceId: packageItem.AppServiceId,
                    AppServicePackageId: packageItem.packageId,
                    Amount: Number(value) || 0
                }
            ]);
        }
    };

    // submit form
    const handleSubmit = (e) => {
        e.preventDefault();
        setBtnLoading(true);
        ApiCreateServiceBuyInvoice(payload)
            .then(({ title, data: resData }) => {
                navigate(mkRelativeUrl(`/billing/invoices/${resData.invoiceId}/info`));
                setBtnLoading(false);
                setMessage({ type: 'success', text: title });
                closeModal();
            })
            .catch((error) => {
                setBtnLoading(false);
                setMessage({ type: 'error', text: error.toString() });
            });
    };

    return (
        <form onSubmit={handleSubmit} className={cn('flex flex-col gap-4 relative', {})}>
            <div
                className={cn({
                    'min-h-40': isLoading
                })}
            >
                {isLoading && (
                    <div className="absolute size-40 top-0 left-1/2 -translate-x-1/2">
                        <ImageUploadingIcon />
                    </div>
                )}
                {!isLoading && servicesList && servicesList?.length === 0 ? (
                    <div className="flex justify-center items-center py-4 bg-secondary-100 rounded-10">
                        <span className="text-gray-500">
                            No service found or user current currency!
                        </span>
                    </div>
                ) : (
                    <div className="max-h-88 overflow-x-auto rounded-10">
                        <Items
                            arrayList={servicesList}
                            handlePackageSelect={handlePackageSelect}
                            handleCustomInput={handleCustomInput}
                            checkedItems={checkedItems}
                        />
                    </div>
                )}
                <div className="w-full p-5 flex justify-between text-[18px] font-medium">
                    <span>Total</span>
                    <span>
                        {GetCurrencySymbol(currency)}
                        {formData.reduce((acc, curr) => {
                            return acc + curr.Amount;
                        }, 0)}
                    </span>
                </div>

                <InputCheckbox
                    label="Make Initiate"
                    name="Start"
                    formData={{ Start: isInitiate }}
                    onChange={(e) => setIsInitiate(e.target.checked)}
                    text="Auto Start?"
                />
            </div>
            <div className="w-full flex justify-end items-center gap-2 mb-2">
                <ButtonCancel title="Cancel" onClick={() => closeModal(false)} />
                <ButtonPrimary title="Create Invoice" btnType="submit" loading={btnLoading} />
            </div>
        </form>
    );
}

export default AddNewServiceModal;
